// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import {
    ApiHandler,
    createHandlerChainRunner,
    HasOnError,
    HasOnSuccess,
    markSkipOnCompleteIfOnSuccessIsSet,
} from "@insite/client-framework/HandlerCreator";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import { CountryModel, StateModel } from "@insite/client-framework/Types/ApiModels";
import {
    AddressValidationApiParameter,
    AddressValidationResult,
    validateAddress,
} from "@rpsc/App/Services/AddressValidation";
import { CustomAppState } from "@rpsc/App/Store/Reducers";
import { getSettingsCollection } from "@rpsc/App/Store/Selectors/SettingsSelectors";

export type AddressValidationHandlerResult = AddressValidationResult & { skipValidation?: boolean };
type HandlerType = ApiHandler<
    AddressValidationApiParameter & HasOnSuccess<AddressValidationHandlerResult> & HasOnError,
    AddressValidationHandlerResult
>;

export const PopulateApiParameters: HandlerType = props => {
    const { address1, address2, address3, address4, city, country, state, postalCode } = props.parameter;
    props.apiParameter = {
        address1,
        address2,
        address3,
        address4,
        city,
        country: (country as any)?.abbreviation || country,
        state: (state as any)?.abbreviation || state,
        postalCode,
    } as AddressValidationApiParameter;
};

export const RequestDataFromApi: HandlerType = async props => {
    const isValidationEnabled = getSettingsCollection(props.getState() as CustomAppState).general
        .addressValidationEnabled;
    if (!isValidationEnabled) {
        props.parameter.onSuccess?.({
            skipValidation: true,
        });
        return false;
    }
    try {
        props.apiResult = await validateAddress(props.apiParameter);
    } catch (error) {
        props.parameter.onSuccess?.(error.errorJson);
    }
};

export const DispatchCompleteLoad: HandlerType = props => {
    markSkipOnCompleteIfOnSuccessIsSet(props);
    props.parameter.onSuccess?.(props.apiResult);
};

export const chain = [PopulateApiParameters, RequestDataFromApi, DispatchCompleteLoad];

const AddressValidation = createHandlerChainRunner(chain, "addressValidation");

export default AddressValidation;
